import React, { useState, useEffect } from "react";
import { DataGrid } from "@mui/x-data-grid";
import Grid from '../components/Grid'; // Assuming Grid is a reusable component for displaying data
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import { FaEye, FaFileInvoiceDollar, FaTimes } from "react-icons/fa"; 
import { CgEnter } from "react-icons/cg";
import axios from "axios";
import { toast } from "react-toastify";
import { saveAs } from "file-saver";
import { FaDownload } from "react-icons/fa"; 
import ExcelJS from "exceljs";


const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function SalesOrders({onAddSalesOrderClick}) {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSearchField, setSelectedSearchField] = useState("salesOrderCode");
  const [salesOrders, setSalesOrders] = useState([]);
  const [selectedSalesOrder, setSelectedSalesOrder] = useState(null);
  const [locations, setLocations] = useState([]);
  const [currentUser, setCurrentUser] = useState("");
  const [userRole, setUserRole] = useState(""); // State to store user role
  const [userId, setUserId] = useState(""); // State to store user ID
  const [organizations, setOrganizations] = useState([]); // List of organizations for Main Admin
  const [showDispatchConfirmation, setShowDispatchConfirmation] = useState(false);
  const [currentDispatchId, setCurrentDispatchId] = useState(null);
  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);
  const [selectedOrderIdToCancel, setSelectedOrderIdToCancel] = useState(null);

  
  // Fetch sales orders from the API
  useEffect(() => {

    // Fetch the logged-in user's role and organization data
    const fetchUserRoleAndOrganizations = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          toast.error("Authentication token is missing. Please log in again.");
          return;
        }

        // Fetch user role
        const userResponse = await axios.get(`${API_BASE_URL}/users/specific`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setUserRole(userResponse.data.role);
        setUserId(userResponse.data._id); 

        // Fetch organizations if the user is a Main Admin
        if (userResponse.data.role === "Main Admin") {
          const orgResponse = await axios.get(`${API_BASE_URL}/organizations`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          setOrganizations(orgResponse.data.organizations || []);
        }
      } catch (error) {
        console.error("Error fetching user role or organizations:", error);
        toast.error("Error fetching data");
      }
    };

    const fetchCurrentUser = async () => {
      const token = localStorage.getItem("token");
          if (!token) {
              toast.error("Authentication token missing. Please log in.");
              return;
          }
          try {
              const response = await axios.get(
                  `${API_BASE_URL}/users/specific`,
                  {
                  headers: { Authorization: `Bearer ${token}` },
                  }
          );
          const userData = response.data;
          setCurrentUser(userData);
          console.log("Userdata:", userData);
          // Fetch locations and products based on role
          if (userData.role === "Main Admin") {
              fetchAllLocations();
          } else if (userData.role === "Company Admin" || "Outlet User") {
              fetchFilteredLocations(userData);
          }
          } catch (error) {
              console.error("Error fetching user data:", error);
              toast.error("Failed to fetch user data.");
          }
  
          fetchCurrentUser();
    };

          const fetchAllLocations = async () => {
          const token = localStorage.getItem("token");
          const response = await axios.get(
              `${API_BASE_URL}/locations`,
              {
                  headers: { Authorization: `Bearer ${token}` },
              }
          );
          setLocations(response.data.locations);
          };
  
          const fetchFilteredLocations = async () => {
          const token = localStorage.getItem("token");
          const response = await axios.get(
              `${API_BASE_URL}/products/filtered-locations`,
              {
              headers: { Authorization: `Bearer ${token}` },
              }
          );
          setLocations(response.data.locations);
      };

    fetchSalesOrders();
    fetchUserRoleAndOrganizations();
  }, [userRole]);

  const handleDownloadExcel = async () => {
  const dataToExport =
    searchTerm.trim().length > 0 ? filteredSalesOrders : salesOrders;

    if (dataToExport.length === 0) {
      toast.warn("No sales order data to export.");
      return;
    }

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Sales Orders");

    const headerRow = [
      "Sales Order Code",
      "Customer Name",
      "Order Request Code",
      "Created Date",
      "Status",
      "Total Amount (LKR)",
      "Tax Amount (LKR)",
      "Final Amount (LKR)",
      "Created By"
    ];
    worksheet.addRow(headerRow);

    const header = worksheet.getRow(1);
    header.eachCell((cell) => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFD966" }, // Light yellow
      };
      cell.font = { bold: true };
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
    });

    dataToExport.forEach((order) => {
      worksheet.addRow([
        order.salesOrderCode,
        order.customer_name || "N/A",
        order.orderRequestCode || "N/A",
        new Date(order.createdAt).toLocaleDateString(),
        order.status || "N/A",
        Number(order.totalAmount || 0).toFixed(2),
        Number(order.taxAmount || 0).toFixed(2),
        Number(order.finalAmount || 0).toFixed(2),
        order.createdBy || "N/A",
      ]);
    });

    worksheet.columns.forEach((column) => {
      column.width = column.header
        ? column.header.toString().length < 20
          ? 20
          : column.header.toString().length
        : 20;
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, "sales_orders.xlsx");
  };

  const fetchSalesOrders = async () => {
      try {
        const token = localStorage.getItem("token");  
        if (!token) {
          toast.error("Authentication token is missing. Please log in again.");
          return;
        }

        const salesOrderResponse = await axios.get(`${API_BASE_URL}/salesOrders/get`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        console.log("All Sales Order Response:", salesOrderResponse.data);

        const formattedSalesOrders = salesOrderResponse.data.salesOrders.map((order) => ({
          id: order.sales_order_id,
          salesOrderCode: order.sales_order_id,
          customer_name: order.customer_id?.customer_name || "Unknown",
          orderRequestCode: order.order_request_id,
          createdAt: order.createdAt,
          products: order.items.map((item) => ({
            product_name: item.product_id?.product_name || "Unknown",
            approved_qty: item.approved_qty,
            total_price: item.total_price,
            unit: item.product_id?.unit?.unit_name,
          })),
          status: order.status,
          totalAmount: order.total_amount,
          taxAmount: order.tax_amount,
          finalAmount: order.final_amount,
          createdBy: order.createdBy?.username || null,
          created_date: order.created_date,
        }));

        setSalesOrders(formattedSalesOrders);

        console.log("Fetched All Sales Orders:", formattedSalesOrders);

      } catch (error) {
        console.error("Error fetching sales orders:", error);
        toast.error("Failed to fetch data. Please try again.");
      }
    };

  const addInvoice = async(sales_order_id) => {

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("Authentication token is missing. Please log in again.");
        return;
      }

      if (!sales_order_id || typeof sales_order_id !== "string") {
        toast.error("Invalid Sales Order ID.");
        return;
      }

      console.log("Fetching Sales Orer ID:", sales_order_id);

      const salesOrderResponse = await axios.get(
        `${API_BASE_URL}/salesOrders/get/${sales_order_id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      console.log("Particular Sales Order Response:", salesOrderResponse.data);

      const salesOrder = salesOrderResponse.data.salesOrder;
      if (!salesOrder) {
        toast.error("Sales order not found.");
        return;
      }

      console.log("Collected Sales Order Data:", salesOrder);

      const dueDate = new Date();
      dueDate.setDate(dueDate.getDate() + 14);
      const formattedDueDate = dueDate.toISOString().split("T")[0];

      // Prepare the invoice data
      const invoiceData = {
        organization: currentUser.role === "Company Admin" ? currentUser.organization._id : null, 
        sales_order_id: salesOrder._id,
        pickup_location_id: salesOrder.pickup_location_id?._id,
        customer_id: salesOrder.customer_id._id,
        payment_receipts: [],
        blance: salesOrder.final_amount,
        amount: salesOrder.final_amount,
        due_date: formattedDueDate,
      };

      console.log("Invoice Data:", invoiceData);

      const response = await axios.post(`${API_BASE_URL}/invoices/add`, invoiceData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      console.log("Invoice added successfully:", response.data);
      toast.success("Invoice added successfully!");
    }
    catch (error) {
      if (error.response && error.response.status === 400) {
        if (error.response.data.message === "An invoice has already been created for this sales order.") {
          toast.error("An invoice has already been created for this sales order.");
        } else if (error.response.data.message === "Cannot create Invoice for a cancelled Sales Order.") {
          toast.error("Cannot create invoice for a cancelled sales order.");
        } else {
          toast.error(error.response.data.message || "Failed to add invoice.");
        }
      } else {
        toast.error("Failed to add invoice. Please try again.");
      }
    }
  }

  const cancelSalesOrder = async(sales_order_id) => {

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("Authentication token is missing. Please log in again.");
        return;
      }

      if (!sales_order_id || typeof sales_order_id !== "string") {
        toast.error("Invalid Sales Order ID.");
        return;
      }

      console.log("Fetching Sales Orer ID:", sales_order_id);

      const salesOrderResponse = await axios.get(
        `${API_BASE_URL}/salesOrders/get/${sales_order_id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      console.log("Particular Sales Order Response:", salesOrderResponse.data);

      const salesOrder = salesOrderResponse.data.salesOrder;
      if (!salesOrder) {
        toast.error("Sales order not found.");
        return;
      }

      console.log("Collected Sales Order Data:", salesOrder);

      // Prepare the invoice data
      const salesOrderData = { 
        sales_order_id: salesOrder._id, // get the sales order object id 
        
      };

      console.log("Invoice Data:", salesOrderData);

      const response = await axios.post(`${API_BASE_URL}/salesOrders/cancel`, {salesOrderData}, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      console.log("Sales order cancelled successfully:", response.data);
      toast.success("Sales Order cancelled successfully!");
      await (fetchSalesOrders());
    }
    catch (error) {
      if (error.response && error.response.status === 400) {
        if (error.response.data.message === "An invoice has already been created for this sales order.") {
          toast.error("Cannot Cancel: An Invoice has already been created.");
        } else {
          toast.error(error.response.data.message || "Failed to cancel Sales Order.");
        }
      } else {
        toast.error("Failed to cancel sales order. Please try again.");
      }
    }
  }

  const handleCancelClick = (id) => {
    setSelectedOrderIdToCancel(id);
    setConfirmCancelOpen(true);
  };

  const confirmCancel = () => {
    cancelSalesOrder(selectedOrderIdToCancel);
    setConfirmCancelOpen(false);
    setSelectedOrderIdToCancel(null);
  };

  const cancelDialog = () => {
    setConfirmCancelOpen(false);
    setSelectedOrderIdToCancel(null);
  };


  const manuallyDispatchSalesOrder = async (sales_order_id) => {
    try {
        const token = localStorage.getItem("token");
        if (!token) {
            toast.error("Authentication token is missing. Please log in again.");
            return;
        }

        if (!sales_order_id || typeof sales_order_id !== "string") {
            toast.error("Invalid Sales Order ID.");
            return;
        }

        console.log("Fetching Sales Order ID:", sales_order_id);

        const response = await axios.get(`${API_BASE_URL}/salesOrders/get/${sales_order_id}`, {
            headers: { Authorization: `Bearer ${token}` },
        });

        const salesOrder = response.data.salesOrder;
        if (!salesOrder) {
            toast.error("Sales order not found.");
            return;
        }

        if (salesOrder.status === "Cancelled") {
          toast.error("Cannot dispatch a sales order that is cancelled.");
          return;
        }

        console.log("Collected Sales Order Data:", salesOrder);
        console.log("Collected Sales Order Items :", salesOrder.items);
        
        let stockUpdateSuccess = true;

        const productQuantities = {}; // { "product_id_location_id": total_requested_qty }
        const productLocationPairs = [];
  
        // Collect product-location pairs from sales orders
       
        for (const item of salesOrder.items) {
          const { product_id, approved_qty } = item;
          const location_id = salesOrder.location_id;

          console.log("location_id:", location_id);
          console.log("product_id:", product_id);

          if (!product_id._id || !location_id._id || isNaN(approved_qty) || approved_qty <= 0) continue;

          const key = `${product_id._id}_${location_id._id}`;
          productLocationPairs.push({ product_id, location_id });

          if (!productQuantities[key]) {
            productQuantities[key] = 0;
          }
          productQuantities[key] += approved_qty; 
        }

        console.log("Total requested product quantities (per location):", productQuantities);

        // Fetch stock levels per product-location pair
        const stockResponses = await axios.post(
          `${API_BASE_URL}/latestStock/getStockByProducts`,
          { product_location_pairs: productLocationPairs },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        console.log("stockResponses:", stockResponses);

        const currentStockData = stockResponses.data; 
        const stockMap = {}; // { "product_id_location_id": { available_stock, product_name } }

        currentStockData.forEach(({ product_id, location_id, updated_stock_qty, product_name, location_name }) => {
          const key = `${product_id}_${location_id}`;
          stockMap[key] = { available_stock: updated_stock_qty || 0, product_name, location_name};
        });

        console.log("Current stock levels (per location):", stockMap);

        // Compare requested quantities with available stock
        for (const key in productQuantities) {
          const [product_id, location_id] = key.split("_");
          const requestedQty = productQuantities[key];
          const availableStock = stockMap[key]?.available_stock || 0;
          const productName = stockMap[key]?.product_name || "Unknown Product";
          const locationName = stockMap[key]?.location_name || "Unknown Location";
  
          if (requestedQty > availableStock) {
            toast.error(`Insufficient stock for ${productName} at ${locationName}. Requested: ${requestedQty}, Available: ${availableStock}`);
            console.error(`Insufficient stock for product ${product_id} at location ${location_id}.`);
            return;
          }
        }

        console.log("Sales Orders:", salesOrders);

        if (Array.isArray(salesOrder.items)) {
            for (const item of salesOrder.items) {
                const { product_id, approved_qty, unit_price } = item;
                if (!product_id || isNaN(approved_qty) || approved_qty <= 0) {
                    console.warn("Invalid sales order item:", item);
                    continue;
                }

                const locationId = salesOrder.location_id || "Location ID not found";
                console.log("Location ID for product", product_id.product_name, "is", locationId);

                const stockMovementData = {
                    movement_type_id: salesOrder._id,
                    sales_order_code: salesOrder.sales_order_id,
                    product_id: item.product_id,
                    location_id: locationId,
                    movement_type: "Remove",
                    movement_name: "sales_order",
                    movement_qty: -approved_qty,
                    updated_price: unit_price,
                    organization_id: salesOrder.organization,
                    performed_by: userId,
                    user_role: userRole,
                };

                console.log("Sending stock movement data for sales order:", stockMovementData);

                try {
                    if (!stockMovementData) {
                        toast.error("Insufficiant Stock ");
                        return;
                    }
                    await axios.post(`${API_BASE_URL}/salesOrders/stockmovement`, stockMovementData, {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            "Content-Type": "application/json",
                        },
                    });

                    toast.success("Sales Order Dispatched successfully!");
                    console.log("Sales Order dsipatched Successfully !")
                } catch (error) {
                    console.error("Error updating stock movement for sales order:", error.response || error.message);

                    // Show a toast with the relevant sales order ID
                    if (error.response && error.response.data) {
                      const { message, salesOrderId } = error.response.data;
                      toast.error(`Error: ${message} for ${salesOrderId}.`);
                    } else {
                      toast.error("Failed to update stock movement.");
                    }
                    stockUpdateSuccess = false;
                    break;
                }
            }

            try{
              const token = localStorage.getItem("token");  
              if (!token) {
                toast.error("Authentication token is missing. Please log in again.");
                return;
              }

              const salesOrderResponse = await axios.get(`${API_BASE_URL}/salesOrders/get`, {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              });

              console.log("All Sales Order Response:", salesOrderResponse.data);

              const formattedSalesOrders = salesOrderResponse.data.salesOrders.map((order) => ({
                id: order.sales_order_id,
                salesOrderCode: order.sales_order_id,
                customer_name: order.customer_id?.customer_name || "Unknown",
                orderRequestCode: order.order_request_id,
                products: order.items.map((item) => ({
                  product_name: item.product_id?.product_name || "Unknown",
                  approved_qty: item.approved_qty,
                  total_price: item.total_price,
                })),
                status: order.status,
                totalAmount: order.total_amount,
                taxAmount: order.tax_amount,
                finalAmount: order.final_amount,
              }));

              setSalesOrders(formattedSalesOrders);

              console.log("Fetched All Sales Orders:", formattedSalesOrders);

            }catch( error ){

            }
        } else {
            console.warn("Sales order items are not an array:", salesOrder);
            stockUpdateSuccess = false; // Ensure we don't attempt to update status if items array is invalid
        }
    } catch (error) {
        console.error("Unexpected error in handleDispatchOrder:", error.response || error.message);
        toast.error("Unexpected error occurred while dispatching Sales order.");
    }
};


  const handleDispatchOrder = async () => {

    console.log("currentDispatchId: ", currentDispatchId);

    if (currentDispatchId) {

      const token = localStorage.getItem("token");
      if (!token) {
          toast.error("Authentication token is missing. Please log in again.");
          return;
      }

      const response = await axios.get(`${API_BASE_URL}/salesOrders/get/${currentDispatchId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const currentDispatchOrder = response.data.salesOrder;
      if (!currentDispatchOrder) {
          toast.error("Sales order not found.");
          return;
      }

      console.log("Collected Dispatched Order Data:", currentDispatchOrder);

      if(currentDispatchOrder.status === "Dispatched") {
        toast.error("This Sales Order Allready Dispatched. ");
        return;
      }else{
        setShowDispatchConfirmation(false);  // Hide the confirmation dialog
        await manuallyDispatchSalesOrder(currentDispatchId);  // Dispatch the sales order
        setCurrentDispatchId(null);  // Reset the dispatch ID
      }

      
    }
  };
  
      const handleOpenDispatchDialog = (sales_order_id) => {
        setShowDispatchConfirmation(true);
        setCurrentDispatchId(sales_order_id);
      };

      const filteredSalesOrders = salesOrders
      .filter((order) => {
        switch (selectedSearchField) {
          case "salesOrderCode":
            return order.salesOrderCode.toLowerCase().includes(searchTerm.toLowerCase());
          case "customer_name":
            return order.customer_name.toLowerCase().includes(searchTerm.toLowerCase());
          case "orderRequestCode":
            return order.orderRequestCode.toLowerCase().includes(searchTerm.toLowerCase());
          case "status":
            return order.status.toLowerCase().includes(searchTerm.toLowerCase());
          case "totalAmount":
            return order.totalAmount.toString().includes(searchTerm);
          case "finalAmount":
            return order.finalAmount.toString().includes(searchTerm);
          case "createdDate":
            return new Date(order.createdAt).toLocaleDateString("en-GB").includes(searchTerm);
          case "createdBy":
            return order.createdBy?.toLowerCase().includes(searchTerm.toLowerCase());
          default:
            return true; // Show all if no filter is applied
        }
      })
      .sort((a, b) => {
        const dateA = new Date(a.createdAt); // Ensure correct field is used
        const dateB = new Date(b.createdAt);
        return dateB - dateA; // Sort latest first
      });

  const columns = [
    // {
    //   field: "checkbox",
    //   headerName: "",
    //   width: 50,
    //   sortable: false,
    //   disableColumnMenu: true,
    //   renderCell: (params) => (
    //     <input
    //       type="checkbox"
    //       onChange={() => console. log("Checkbox clicked for:", params.row.id)}
    //     />
    //   ),
    // },
    { field: "salesOrderCode", headerName: "Sales Order ID", flex: 1 },
    {
      field: "created_date",
      headerName: "Created Date",
      flex: 1,
      renderCell: (params) => (
        <span>
          {params.value ? new Date(params.value).toLocaleDateString("en-GB") : "N/A"}
        </span>
      ),
    },
    { field: "customer_name", headerName: "Customer Name", flex: 1 },
    { field: "orderRequestCode", headerName: "Order Request ID", flex: 1 },
    { field: "totalAmount", headerName: "Total Amount (LKR)", flex: 1, headerAlign: "right", align: "right", renderCell: (params) => `${new Intl.NumberFormat('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(params.value)}` },
    { field: "taxAmount", headerName: "Tax Amount (LKR)", flex: 1, headerAlign: "right", align: "right", renderCell: (params) => `${new Intl.NumberFormat('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(params.value)}` },
    { field: "finalAmount", headerName: "Final Amount (LKR)", flex: 1, headerAlign: "right", align: "right", renderCell: (params) => `${new Intl.NumberFormat('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(params.value)}` },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        const status = params.row.status;
    
        // Map statuses to classes
        const statusClassMap = {
          "Drafted": "bg-gray-400 text-white",
          "TO Created": "bg-blue-400 text-white",
          "Dispatched": "bg-green-400 text-white",
        };
    
        // Get the class based on status or default to a neutral color
        const statusClass = statusClassMap[status] || "bg-gray-500 text-white"; // Default class if status not found
    
        return (
          <span className={`px-2 py-1 rounded-sm ${statusClass}`}>
            {status}
          </span>
        );
      },
    },
    {
      field: "createdBy",
      headerName: "Created By",
      flex: 1,
    },
    {
      field: "action",
      headerName: "Action",
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <div className="flex justify-center items-center p-3 space-x-3">
          <button
            onClick={() => handleCancelClick(params.row.id)}
            className="bg-[#d82828] text-white p-2 flex items-center justify-center"
          >
            <FaTimes className="text-white" />
          </button>
          <button
            onClick={() => setSelectedSalesOrder(params.row)}
            className="bg-[#FBC02D] text-white p-2 flex items-center justify-center"
          >
            <FaEye className="text-white" />
          </button>
          <button
            onClick={() => addInvoice(params.row.id)}
            className="bg-[#4CAF50] text-white p-2 flex items-center justify-center"
          >
            <FaFileInvoiceDollar className="text-white" />
          </button>
          <button
            onClick={() => handleOpenDispatchDialog(params.row.id)}
            className="bg-[#8BC34A] text-white p-2 flex items-center justify-center"
          >
            <CgEnter className="text-white" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="flex h-screen">
      {/* Main Content */}
      <div className="flex-1 p-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-green-700">Sales Orders</h1>
        </div>
  
        <div className="bg-white p-8 rounded-lg shadow mb-8">
          <div className="flex justify-between mb-4 items-center flex-wrap gap-4">
            {/* Left Section: Search filters */}
            <div className="flex items-center flex-wrap gap-4">
              <select
                className="px-4 py-2 border border-gray-300 rounded-lg"
                value={selectedSearchField}
                onChange={(e) => setSelectedSearchField(e.target.value)}
              >
                <option value="salesOrderCode">Sales Order Code</option>
                <option value="customer_name">Customer Name</option>
                <option value="orderRequestCode">Order Request Code</option>
                <option value="status">Status</option>
                <option value="totalAmount">Total Amount</option>
                <option value="finalAmount">Final Amount</option>
                <option value="createdDate">Created Date</option>
                <option value="createdBy">Created By</option>
              </select>

              <div className="relative">
                <input
                  type="text"
                  placeholder="Search Sales Orders"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="px-4 py-2 pl-10 border border-gray-300 rounded-lg w-full max-w-xs"
                />
                <div className="absolute inset-y-0 left-0 flex items-center pl-3">
                  <FontAwesomeIcon icon={faSearch} className="text-gray-500" />
                </div>
              </div>
            </div>

            {/* Right Section: Download Excel */}
            <div>
              <button
                onClick={handleDownloadExcel}
                className="flex items-center bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
              >
                <FaDownload className="mr-2" />
                Download Excel
              </button>
            </div>
          </div>        
          <div>
            <Grid rows={filteredSalesOrders} columns={columns} getRowId={(row) => row.salesOrderCode} />
          </div>
        </div>
  
        {selectedSalesOrder && (
          <div
            className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50 z-50"
            style={{ overflowY: "auto" }}
          >
            <div
              className="bg-white p-8 rounded-lg shadow-lg w-full md:w-2/3 lg:w-1/2 relative max-h-[90vh] overflow-y-auto"
              style={{ maxHeight: "90vh", overflowY: "auto" }}
            >
              <button
                onClick={() => setSelectedSalesOrder(null)}
                className="absolute top-2 right-2 bg-red-500 text-white w-8 h-8 flex justify-center items-center rounded-lg hover:bg-red-700"
              >
                &times;
              </button>
              <h2 className="text-xl font-semibold mb-4">
                Sales Order Details: {selectedSalesOrder.salesOrderCode}
              </h2>
              <div>
                <p>
                  <strong>Customer Name:</strong> {selectedSalesOrder.customer_name}
                </p>
                <p>
                  <strong>Total Amount:</strong> LKR{" "}
                  {Number(selectedSalesOrder.totalAmount).toFixed(2)}
                </p>
                <p>
                  <strong>Tax Amount:</strong> LKR{" "}
                  {Number(selectedSalesOrder.taxAmount).toFixed(2)}
                </p>
                <p>
                  <strong>Final Amount:</strong> LKR{" "}
                  {Number(selectedSalesOrder.finalAmount).toFixed(2)}
                </p>
                <h3 className="text-lg font-semibold mt-4 mb-2">Products</h3>
                <div style={{ height: 400, width: "100%" }}>
                  <DataGrid
                    rows={selectedSalesOrder.products.map((product, index) => ({
                      id: index + 1,
                      productName: product.product_name,
                      approvedQty: product.approved_qty,
                      unit: product.unit,
                      totalPrice: parseFloat(product.total_price).toFixed(2),
                    }))}
                    columns={[
                      { field: "id", headerName: "ID", width: 80 },
                      { field: "productName", headerName: "Product Name", width: 150 },
                      { field: "approvedQty", headerName: "Approved Qty", width: 120, headerAlign: "right", align: "right" },
                      { 
                        field: "unit", 
                        headerName: "Units", 
                        flex: 1, 
                        headerAlign: "center", 
                        align: "center" 
                      },
                      { field: "totalPrice", headerName: "Total Price (LKR)", width: 140, headerAlign: "right", align: "right" },
                    ]}
                    pageSize={4}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {showDispatchConfirmation && (
      <div className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50 z-50">
        <div className="bg-white p-6 rounded-lg shadow-lg">
          <h2 className="text-lg font-semibold mb-4">Confirm Dispatch</h2>
          <p>Are you sure you want to dispatch this sales order?</p>
          <div className="flex justify-end mt-4">
            <button
              onClick={() => setShowDispatchConfirmation(false)}
              className="mr-4 px-4 py-2 bg-gray-500 text-white rounded-lg"
            >
              Cancel
            </button>
            <button
              onClick={handleDispatchOrder}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg"
            >
              Confirm
            </button>
          </div>
        </div>
      </div>
    )}

    {confirmCancelOpen && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white p-6 rounded-lg shadow-md w-80">
          <h2 className="text-lg font-semibold mb-4">Confirm Cancellation</h2>
          <p className="mb-6">Are you sure you want to cancel this sales order?</p>
          <div className="flex justify-end space-x-4">
            <button onClick={cancelDialog} className="bg-gray-300 px-4 py-2 rounded">Cancel</button>
            <button onClick={confirmCancel} className="bg-red-600 text-white px-4 py-2 rounded">Confirm</button>
          </div>
        </div>
      </div>
    )}
    </div>
  );
}

export default SalesOrders;